import React, { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Container, Typography, Grid, Button, Box, useTheme } from "@mui/material";
import WifiIcon from "@mui/icons-material/Wifi";
import LoginIcon from "@mui/icons-material/Login";
import LogoutIcon from "@mui/icons-material/Logout";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PeopleIcon from "@mui/icons-material/People";
import BedIcon from "@mui/icons-material/Bed";
import LocalParkingIcon from "@mui/icons-material/LocalParking";
import PetsIcon from "@mui/icons-material/Pets";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import GroupsIcon from "@mui/icons-material/Groups";
import HolidayVillageIcon from "@mui/icons-material/HolidayVillage";
import CheckIcon from "@mui/icons-material/Check";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import servicios from "./servicios";
import images from "./images";
import ImageModal from "../../components/ImageModal";
import VideoCard from "../../components/VideoCard";
import Reveal from "../../components/Reveal";
import {
  PageHero,
  Section,
  SectionTitle,
  ZoomImage,
} from "../../components/layout";
import CONTACT_INFO from "../../constants/contactInfo";

const HERO_IMAGE =
  "https://ik.imagekit.io/cabanasdelcampo/interiores/DCS_3079_1.jpg?updatedAt=1760707539394";

const HIGHLIGHTS = [
  { icon: HolidayVillageIcon, value: "2", label: "cabañas" },
  { icon: PeopleIcon, value: "4", label: "personas máx. por cabaña" },
  { icon: LoginIcon, value: "14 h", label: "check in" },
  { icon: LogoutIcon, value: "11 h", label: "check out" },
];

const GENERALES = [
  "Un delicioso desayuno seco que ofrece una amplia variedad de opciones frescas y sabrosas.",
  "Piscina y solarium al aire libre.",
  "Atención 24 horas.",
  "Juegos de mesa (solicitar en recepción).",
];

const NORMAS = [
  { icon: WifiIcon, title: "Internet", text: "Libre acceso a wifi." },
  { icon: LocalParkingIcon, title: "Parking", text: "Estacionamiento cubierto." },
  { icon: PetsIcon, title: "Mascotas", text: "No se aceptan." },
  {
    icon: BedIcon,
    title: "Ropa blanca",
    text: "Las toallas proporcionadas son solo para uso dentro de la cabaña, no para la pileta.",
  },
  {
    icon: GroupsIcon,
    title: "No se permiten visitas",
    text: "Solo las personas alojadas en la cabaña podrán hacer uso de las instalaciones.",
  },
  { icon: PeopleIcon, title: "Solo para adultos", text: "El alojamiento es exclusivo para mayores de edad." },
];

const PAGO = [
  "La reserva se confirma con un depósito o transferencia del 50% del total de la estadía. Este monto no es reembolsable.",
  "El saldo se abona en efectivo al momento de ingresar a la cabaña.",
  "Por el momento no trabajamos con tarjetas de crédito o débito.",
  "En caso de no poder asistir a la reserva, se podrá reprogramar la estadía, sujeto a la disponibilidad del establecimiento.",
];

// Foto clickeable que abre el modal
const GalleryImage = ({ image, onClick, sx }) => (
  <ZoomImage
    onClick={() => onClick(image)}
    sx={{
      height: "100%",
      cursor: "zoom-in",
      borderRadius: "20px",
      "&::after": {
        content: '""',
        position: "absolute",
        inset: 0,
        background: "rgba(0,0,0,0)",
        transition: "background .4s ease",
      },
      "&:hover::after": { background: "rgba(0,0,0,0.15)" },
      "&:hover .zoom-icon": { opacity: 1, transform: "scale(1)" },
      ...sx,
    }}
  >
    <img src={image.url} alt={image.title} loading="lazy" />
    <Box
      className="zoom-icon"
      sx={{
        position: "absolute",
        right: 12,
        bottom: 12,
        zIndex: 1,
        width: 40,
        height: 40,
        borderRadius: "50%",
        display: "grid",
        placeItems: "center",
        backgroundColor: "rgba(255,255,255,0.9)",
        opacity: 0,
        transform: "scale(0.8)",
        transition: "opacity .3s ease, transform .3s ease",
      }}
    >
      <ZoomInIcon fontSize="small" />
    </Box>
  </ZoomImage>
);

const IconBadge = ({ icon: Icon, dark }) => (
  <Box
    sx={{
      width: 48,
      height: 48,
      flexShrink: 0,
      borderRadius: "50%",
      display: "grid",
      placeItems: "center",
      backgroundColor: dark ? "rgba(255,255,255,0.1)" : "rgba(203,110,40,0.12)",
      color: dark ? "primary.light" : "primary.main",
    }}
  >
    <Icon />
  </Box>
);

const Alojamiento = () => {
  const theme = useTheme();
  const [selectedImage, setSelectedImage] = useState(null);

  const cardSx = {
    backgroundColor: "background.paper",
    borderRadius: "20px",
    border: `1px solid ${theme.palette.sand.dark}`,
  };

  return (
    <Box>
      <PageHero image={HERO_IMAGE} overline="Alojamiento" title="Nuestras Cabañas">
        Contamos con 2 acogedoras cabañas equipadas para 4 personas como máximo,
        diseñadas para brindarte una experiencia única en medio de la naturaleza.
      </PageHero>

      {/* Datos rápidos */}
      <Container sx={{ mt: { xs: -4, md: -6 }, position: "relative", zIndex: 1 }}>
        <Reveal>
          <Box
            sx={{
              ...cardSx,
              border: "none",
              boxShadow: "0 20px 50px -20px rgba(35,31,32,0.25)",
              display: "grid",
              gridTemplateColumns: { xs: "repeat(2, 1fr)", md: "repeat(4, 1fr)" },
              gap: { xs: 3, md: 2 },
              p: { xs: 3, md: 4 },
            }}
          >
            {HIGHLIGHTS.map((item) => (
              <Box key={item.label} sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <IconBadge icon={item.icon} />
                <Box>
                  <Typography
                    sx={{
                      fontFamily: theme.typography.h1.fontFamily,
                      fontSize: "1.6rem",
                      fontWeight: 600,
                      lineHeight: 1.1,
                    }}
                  >
                    {item.value}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary", lineHeight: 1.3 }}>
                    {item.label}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Reveal>
      </Container>

      {/* Galería */}
      <Section>
        <Container>
          <Reveal>
            <SectionTitle overline="Por dentro" title="Conocé las cabañas">
              Espacios cálidos, luminosos y pensados al detalle. Tocá una foto
              para verla en grande.
            </SectionTitle>
          </Reveal>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "repeat(2, 1fr)", md: "1fr 300px 1fr" },
              gridAutoRows: { xs: 172, md: 260 },
              gap: 2,
              alignItems: "stretch",
            }}
          >
            <Reveal sx={{ gridColumn: { md: 1 }, gridRow: { md: 1 } }}>
              <GalleryImage image={images[0]} onClick={setSelectedImage} />
            </Reveal>
            <Reveal delay={80} sx={{ gridColumn: { md: 1 }, gridRow: { md: 2 } }}>
              <GalleryImage image={images[1]} onClick={setSelectedImage} />
            </Reveal>
            <Reveal
              delay={120}
              sx={{
                gridColumn: { xs: "span 2", md: 2 },
                gridRow: { xs: "span 3", md: "1 / 3" },
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <VideoCard videoUrl="https://www.youtube.com/embed/JuVVhn_CuGc?autoplay=1&mute=1" />
            </Reveal>
            <Reveal delay={160} sx={{ gridColumn: { md: 3 }, gridRow: { md: 1 } }}>
              <GalleryImage image={images[2]} onClick={setSelectedImage} />
            </Reveal>
            <Reveal delay={200} sx={{ gridColumn: { md: 3 }, gridRow: { md: 2 } }}>
              <GalleryImage image={images[3]} onClick={setSelectedImage} />
            </Reveal>
            {images.slice(4, 7).map((image, index) => (
              <Reveal
                key={image.url}
                delay={index * 80}
                sx={{
                  gridColumn: {
                    xs: index === 2 ? "span 2" : "auto",
                    md: ["1", "2 / 4", "auto"][index],
                  },
                  display: { md: index === 2 ? "none" : "block" },
                }}
              >
                <GalleryImage image={image} onClick={setSelectedImage} />
              </Reveal>
            ))}
          </Box>
        </Container>
      </Section>

      <ImageModal
        open={selectedImage !== null}
        onClose={() => setSelectedImage(null)}
        image={selectedImage}
      />

      {/* Servicios */}
      <Section sx={{ backgroundColor: theme.palette.sand.light, py: { xs: 8, md: 10 } }}>
        <Container>
          <Grid container spacing={{ xs: 3, md: 8 }}>
            <Grid item xs={12} md={4}>
              <Reveal>
                <SectionTitle overline="Comodidades" title="Servicios incluidos" sx={{ mb: 0 }}>
                  Todo lo que necesitás para sentirte como en casa, en el medio
                  de las sierras.
                </SectionTitle>
              </Reveal>
            </Grid>
            <Grid item xs={12} md={8}>
              <Reveal delay={100}>
                <Box
                  component="ul"
                  sx={{
                    listStyle: "none",
                    p: 0,
                    m: 0,
                    display: "grid",
                    gridTemplateColumns: { xs: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" },
                    columnGap: { xs: 2, md: 4 },
                  }}
                >
                  {servicios.map((servicio) => (
                    <Box
                      component="li"
                      key={servicio.name}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: { xs: 1, md: 1.5 },
                        py: 1.5,
                        borderBottom: `1px solid ${theme.palette.sand.dark}`,
                        "& .MuiSvgIcon-root": { color: "primary.main", fontSize: 22 },
                      }}
                    >
                      {servicio.icon}
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {servicio.name}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Reveal>
            </Grid>
          </Grid>
        </Container>
      </Section>

      {/* Condiciones */}
      <Section>
        <Container>
          <Reveal>
            <SectionTitle overline="Antes de venir" title="Condiciones" />
          </Reveal>
          <Grid container spacing={4}>
            <Grid item xs={12} md={5}>
              <Reveal>
                <Typography variant="h5" sx={{ mb: 3 }}>
                  Incluido en tu estadía
                </Typography>
                <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0 }}>
                  {GENERALES.map((item) => (
                    <Box
                      component="li"
                      key={item}
                      sx={{ display: "flex", gap: 1.5, mb: 2, alignItems: "flex-start" }}
                    >
                      <CheckIcon sx={{ color: "secondary.main", mt: 0.5 }} fontSize="small" />
                      <Typography variant="body1">{item}</Typography>
                    </Box>
                  ))}
                </Box>

                <Box
                  sx={{
                    mt: 4,
                    p: 3,
                    borderRadius: "20px",
                    backgroundColor: "rgba(203,110,40,0.08)",
                    borderLeft: `4px solid ${theme.palette.primary.main}`,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                    <ShoppingCartIcon sx={{ color: "primary.main" }} />
                    <Typography variant="h6">Proveeduría</Typography>
                  </Box>
                  <Typography variant="body2" sx={{ mb: 1.5 }}>
                    Disponemos de una proveeduría en el lugar para ofrecerte
                    productos esenciales de consumo diario.
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                    <InfoOutlinedIcon sx={{ color: "primary.main", mt: 0.3 }} fontSize="small" />
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                      Te sugerimos traer todos los ingredientes y productos
                      necesarios para cocinar antes de llegar: carnes, verduras,
                      frutas, alimentos no perecederos, bebidas y cualquier otro
                      insumo que consideres importante. Recordá que los lugares
                      más cercanos para comer o comprar se encuentran a 20 km de
                      distancia.
                    </Typography>
                  </Box>
                </Box>
              </Reveal>
            </Grid>
            <Grid item xs={12} md={7}>
              <Reveal delay={100}>
                <Typography variant="h5" sx={{ mb: 1 }}>
                  Normas de la casa
                </Typography>
                <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0 }}>
                  {NORMAS.map((norma) => (
                    <Box
                      component="li"
                      key={norma.title}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        py: 2,
                        borderBottom: `1px solid ${theme.palette.sand.dark}`,
                      }}
                    >
                      <IconBadge icon={norma.icon} />
                      <Box>
                        <Typography variant="h6" sx={{ fontSize: "1.05rem" }}>
                          {norma.title}
                        </Typography>
                        <Typography variant="body2" sx={{ color: "text.secondary" }}>
                          {norma.text}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Reveal>
            </Grid>
          </Grid>
        </Container>
      </Section>

      {/* Reserva y formas de pago */}
      <Section sx={{ backgroundColor: theme.palette.secondary.dark, mb: "-20px" }}>
        <Container>
          <Grid container spacing={{ xs: 4, md: 10 }} alignItems="center">
            <Grid item xs={12} md={5}>
              <Reveal>
                <SectionTitle overline="Reservas" title="Reserva y formas de pago" light sx={{ mb: 4 }} />
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                  <Button
                    variant="contained"
                    size="large"
                    href={CONTACT_INFO.reservarLink}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Consultar disponibilidad
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    component={RouterLink}
                    to="/contacto"
                    sx={{
                      color: "white",
                      borderColor: "rgba(255,255,255,0.6)",
                      "&:hover": { borderColor: "white", backgroundColor: "rgba(255,255,255,0.08)" },
                    }}
                  >
                    Consultá nuestras tarifas
                  </Button>
                </Box>
              </Reveal>
            </Grid>
            <Grid item xs={12} md={7}>
              <Reveal delay={150}>
                <Box component="ol" sx={{ listStyle: "none", p: 0, m: 0 }}>
                  {PAGO.map((item, index) => (
                    <Box
                      component="li"
                      key={item}
                      sx={{
                        display: "flex",
                        gap: 3,
                        py: 2.5,
                        borderTop: index ? "1px solid rgba(255,255,255,0.15)" : "none",
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: theme.typography.h1.fontFamily,
                          fontSize: "1.5rem",
                          color: "primary.light",
                          lineHeight: 1.2,
                          minWidth: 32,
                        }}
                      >
                        {String(index + 1).padStart(2, "0")}
                      </Typography>
                      <Typography sx={{ color: "rgba(255,255,255,0.9)" }}>{item}</Typography>
                    </Box>
                  ))}
                </Box>
              </Reveal>
            </Grid>
          </Grid>
        </Container>
      </Section>
    </Box>
  );
};

export default Alojamiento;
